/**
 * One turn in the transcript.
 *
 * Speaker distinction is carried by three things at once — an avatar, a named
 * label, and a different surface — rather than by colour alone, so the
 * conversation still reads correctly in high contrast and for anyone who cannot
 * distinguish the two backgrounds.
 *
 * Line length is capped in the page container, not here; a chat transcript that
 * runs the full width of a desktop monitor is unreadable at body size.
 */

import { Bot, Icon, UserRound } from "@norican/icons";
import { Avatar, Card, cn, Typography } from "@norican/react";
import type { ReactNode } from "react";

import { RelativeTime } from "@/components/common";
import { useT } from "@/i18n";

import type { AssistantRole } from "./contract";

export interface MessageBubbleProps {
  role: AssistantRole;
  /** ISO timestamp shown beside the speaker name. */
  timestamp?: string | null;
  /** Set while a reply is still arriving, so assistive tech knows it is unfinished. */
  busy?: boolean;
  children: ReactNode;
  /** Row of controls under the content (feedback, copy, retry). */
  footer?: ReactNode;
  className?: string;
}

export function MessageBubble({
  role,
  timestamp,
  busy = false,
  children,
  footer,
  className,
}: MessageBubbleProps) {
  const { t } = useT("assistant");
  const isUser = role === "USER";
  const speaker = isUser ? t("message.you") : t("message.assistant");

  return (
    <article
      className={cn("flex w-full gap-3", className)}
      aria-label={speaker}
      aria-busy={busy || undefined}
    >
      <Avatar.Root size="sm" className="mt-1 shrink-0">
        <Avatar.Fallback>
          <Icon icon={isUser ? UserRound : Bot} size="sm" tone={isUser ? undefined : "accent"} />
        </Avatar.Fallback>
      </Avatar.Root>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <Typography variant="label">{speaker}</Typography>
          {timestamp === null || timestamp === undefined ? null : (
            <Typography as="span" variant="caption" tone="muted">
              <RelativeTime value={timestamp} />
            </Typography>
          )}
        </div>

        <Card variant={isUser ? "subtle" : "outline"} padding="md">
          <div className="flex flex-col gap-4">{children}</div>
        </Card>

        {footer === undefined ? null : <div className="pt-1">{footer}</div>}
      </div>
    </article>
  );
}
